import VideoPlayer from "@/src/components/posts/videos/video-player";
import { BlurView } from "expo-blur";
import { ArrowLeft2, ArrowRight2, CloseCircle } from "iconsax-react-nativejs";
import React, { useCallback, useState } from "react";
import {
  Modal,
  Pressable,
  ScrollView,
  StatusBar,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useTheme } from "@/src/styles/theme-provider";

const MAX_DOTS = 20;

type VideoLightboxProps = Readonly<{
  videos: string[];
  index: number;
  onClose: () => void;
}>;

const VideoLightbox = ({ videos, index, onClose }: VideoLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(index);
  const { themeVars, varStyle } = useTheme();

  const totalVideos = videos.length;
  const counterLabel = `${currentIndex + 1} / ${totalVideos}`;
  const remainingVideos = totalVideos - MAX_DOTS;
  const hasMoreThanMaxDots = totalVideos > MAX_DOTS;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === totalVideos - 1;

  const goToPrevious = useCallback(() => {
    if (!isFirst) setCurrentIndex((prev) => prev - 1);
  }, [isFirst]);

  const goToNext = useCallback(() => {
    if (!isLast) setCurrentIndex((prev) => prev + 1);
  }, [isLast]);

  const goToIndex = (i: number) => setCurrentIndex(i);

  return (
    <Modal
      visible
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <StatusBar barStyle="light-content" />
      <Pressable style={varStyle} className="flex-1" onPress={onClose}>
        <BlurView intensity={15} tint="dark" className="flex-1">
          {/* Header */}
          <View className="flex-row justify-between items-center mt-12 mx-4 mb-2">
            <Text className="text-white/60 text-xs font-medium tracking-widest">
              {counterLabel}
            </Text>
            <TouchableOpacity onPress={onClose} activeOpacity={0.7} className="p-1.5 rounded-full hover:bg-ash-3">
              <CloseCircle size={20} color={themeVars["--color-slate-default"] as string} />
            </TouchableOpacity>
          </View>

          {/* Video + arrows */}
          <Pressable className="flex-1 flex-row items-center" onPress={onClose}>
            <TouchableOpacity
              onPress={goToPrevious}
              disabled={isFirst}
              activeOpacity={0.7}
              className={`px-4 ${isFirst ? "opacity-30" : ""}`}
            >
              <View className="w-10 h-10 rounded-full bg-white/80 items-center justify-center hover:bg-ash-3">
                <ArrowLeft2 size={20} color={themeVars["--color-slate-default"] as string} />
              </View>
            </TouchableOpacity>

            <Pressable
              className="flex-1 items-center justify-center"
              onPress={(e) => e.stopPropagation()}
            >
              <VideoPlayer
                key={currentIndex}
                uri={videos[currentIndex]}
                height={300}
              />
            </Pressable>

            <TouchableOpacity
              onPress={goToNext}
              disabled={isLast}
              activeOpacity={0.7}
              className={`px-4 ${isLast ? "opacity-30" : ""}`}
            >
              <View className="w-10 h-10 rounded-full bg-white/80 items-center justify-center hover:bg-ash-3">
                <ArrowRight2 size={20} color={themeVars["--color-slate-default"] as string} />
              </View>
            </TouchableOpacity>
          </Pressable>

          {/* Dots */}
          <Pressable
            className="pb-10 pt-3 items-center gap-3"
            onPress={onClose}
          >
            <Pressable onPress={(e) => e.stopPropagation()}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerClassName="justify-center gap-1.5 px-4"
              >
                {videos.slice(0, MAX_DOTS).map((_, i) => (
                  <TouchableOpacity
                    key={i}
                    onPress={() => goToIndex(i)}
                    activeOpacity={0.7}
                  >
                    <View
                      className={`h-1.5 rounded-full ${
                        i === currentIndex
                          ? "bg-white w-4"
                          : "bg-white/30 w-1.5"
                      }`}
                    />
                  </TouchableOpacity>
                ))}
                {hasMoreThanMaxDots && (
                  <Text className="text-white/30 text-xs self-center ml-1">
                    +{remainingVideos}
                  </Text>
                )}
              </ScrollView>
            </Pressable>
          </Pressable>
        </BlurView>
      </Pressable>
    </Modal>
  );
};

export default VideoLightbox;
